import React, { useState } from 'react';

import { useAppDispatch } from '../hooks';

import { TombstoneActions } from '../actions/tombstone/tombstone.actions';

import type { Tombstone } from '../interfaces/client/tombstone.interface';

import Modal from './components/Modal';
import { num, formatDate } from './lib/mandate-helpers';

// =============================================================================
//  Tombstone edit modal (v3)
//
//  Edits the four fields that get adjusted after a deal closes:
//
//    bsm_role          — free-text dropdown ("Sell-side advisor", etc.)
//    deal_type         — broad chip ("M&A", "Listing", "Capital Raise" ...)
//    size_of_transaction — ZAR amount (number input — formatted on display)
//    date              — close date (YYYY-MM-DD)
// =============================================================================

const ROLE_OPTIONS = [
  'Sell-side advisor',
  'Buy-side advisor',
  'Lead advisor',
  'JSE Sponsor',
  'Sole financial advisor',
  'Independent advisor'
] as const;

const TYPE_OPTIONS = [
  'M&A',
  'Listing',
  'Capital Raise',
  'Restructuring',
  'Valuation',
  'BEE',
  'Strategic Advice',
  'Independent Business Review',
  'Research'
] as const;

interface Props {
  tombstone: Tombstone;
  onClose: () => void;
}

const isoDate = (input: Tombstone['date']): string => {
  if (!input) return '';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const TombstoneEditModal = ({ tombstone, onClose }: Props) => {
  const dispatch = useAppDispatch();

  const [bsmRole, setBsmRole] = useState<string>(tombstone.bsm_role || 'Lead advisor');
  const [dealType, setDealType] = useState<string>(tombstone.deal_type || 'M&A');
  const [size, setSize] = useState<string>(String(num(tombstone.size_of_transaction) || ''));
  const [date, setDate] = useState<string>(isoDate(tombstone.date));
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    dispatch(
      TombstoneActions.updateTombstone(
        tombstone.id as number | string,
        {
          bsm_role: bsmRole,
          deal_type: dealType,
          size_of_transaction: size === '' ? 0 : Number(size),
          date
        } as Partial<Tombstone> as Tombstone,
        () => {
          setSaving(false);
          onClose();
        }
      )
    );
  };

  const footer = (
    <>
      <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
        Cancel
      </button>
      <button type="button" className="btn primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </>
  );

  return (
    <Modal
      title={`Edit tombstone — ${tombstone.client || '#' + tombstone.id}`}
      onClose={onClose}
      footer={footer}
    >
      <div className="field">
        <span className="label">BSM role</span>
        <select value={bsmRole} onChange={(e) => setBsmRole(e.target.value)}>
          {ROLE_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <span className="label">Deal type</span>
        <select value={dealType} onChange={(e) => setDealType(e.target.value)}>
          {TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <span className="label">Transaction size (ZAR)</span>
        <input
          type="number"
          min={0}
          step={1000}
          value={size}
          onChange={(e) => setSize(e.target.value)}
          placeholder="e.g. 1800000000"
        />
      </div>

      <div className="field">
        <span className="label">Date</span>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <span
          className="muted"
          style={{ fontSize: 11.5, marginTop: 2, textTransform: 'none', letterSpacing: 0 }}
        >
          Currently displayed as {formatDate(date || tombstone.date)}.
        </span>
      </div>

      <p className="muted" style={{ fontSize: 12, lineHeight: 1.4, marginTop: 4, marginBottom: 0 }}>
        Editing client, category, or linked people stays in the legacy form under{' '}
        <code>
          /core/clients/{tombstone.client_id || ':client_id'}/tombstones/
          {tombstone.id}
        </code>
        .
      </p>
    </Modal>
  );
};

export default TombstoneEditModal;
